import { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { DateObject } from 'react-multi-date-picker'

import { useGetByTenantOrderConfirmedQuery } from '@/api/rtk-api'
import { last30Days, Rule, TablePagination } from '@/components/core-v2'
import { TENANT } from '@/const'

import { DEFAULT_QUERY } from '../assets-filter-list/orders-filter-context'
import { OrderEconomics } from '../order-economics/order-economics'
import { GroupedTab } from './grouped-tabs-panel'
import { OrderConfirmedDetails } from './order-confirmed-details'
import { OrderTrades } from './order-trades'
import { OrdersDetailsTemplate } from './orders-details-template'
import { OrdersFilters } from './orders-filters'
import { ConfirmedOrder, OrderType } from './orders-types'

export interface OrdersConfirmedProps {
  assetId: string
}

export const getDate = (dateRange: Date | DateObject) =>
  dateRange instanceof Date ? dateRange.toISOString().split('T')[0] : dateRange.format('YYYY-MM-DD')

export const OrdersConfirmed = ({ assetId }: OrdersConfirmedProps) => {
  const form = useForm({
    defaultValues: { orderTypeFilter: 'all', ordersDatePicker: last30Days.value }
  })

  const orderTypeFilter = form.watch('orderTypeFilter')
  const ordersDatePicker = form.watch('ordersDatePicker')
  const [pagination, setPagination] = useState({
    pageSize: DEFAULT_QUERY.pageSize,
    pageNumber: DEFAULT_QUERY.pageNumber
  })

  const { data, isLoading } = useGetByTenantOrderConfirmedQuery({
    assetId,
    creationStartDateInclusive: getDate(ordersDatePicker[0]),
    creationEndDateInclusive: getDate(ordersDatePicker[1]),
    includeRedemptionOrders: orderTypeFilter === 'all' || orderTypeFilter === 'sell',
    includeSubscriptionOrders: orderTypeFilter === 'all' || orderTypeFilter === 'buy',
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    tenant: TENANT
  })

  const getTabs = useCallback(
    (order: ConfirmedOrder): [GroupedTab, GroupedTab, GroupedTab] => [
      {
        component: <OrderConfirmedDetails order={order} />,
        heading: 'Details',
        value: 'details'
      },
      {
        component: <OrderEconomics order={order} />,
        heading: 'Economics',
        value: 'economics'
      },
      {
        component: <OrderTrades order={order} />,
        heading: 'Trades',
        value: 'trades'
      }
    ],
    []
  )

  const onPage = p => setPagination(p)
  return (
    <div className="mx-12 mt-4">
      <FormProvider {...form}>
        <OrdersFilters />
      </FormProvider>
      <Rule className="my-3" />
      <TablePagination
        pagination={{ ...pagination, totalRows: data?.totalDocuments ?? 0 }}
        onChange={onPage}
      />
      <Rule className="my-3" />
      <OrdersDetailsTemplate
        getTabs={getTabs}
        isLoading={isLoading}
        orders={data?.data}
        type={OrderType.Confirmed}
      />
    </div>
  )
}
